import type { IntlShape } from 'react-intl'
import type {
  TransportOption,
  TripDay,
  TripItineraryData,
  TripItineraryItem,
  TripItemWithoutDay,
  TripStop,
} from 'tripPlanner/api/types'
import type { GooglePlacesPriceLevel } from 'tripPlanner/types/common'
import { buildFlightName } from '../../utils/transportNames'
import { getLocalisedDayLabel } from 'tripPlanner/utils/chatDates'
import { isExperienceSavedData, isHotelSavedData } from 'tripPlanner/utils/savedItemDataGuards'
import type {
  TripListFilters,
  TripListGroup,
  TripListOfferTarget,
  TripListPrice,
  TripListRow,
  TripListRowType,
  TripListSort,
} from './tripListTypes'
import { getTripListPlaceCategory, getTripListRowTypeFromPlaceCategory } from './tripListPlaceCategories'
import { getTripListTypeLabel } from './tripListTypeLabels'

const TRANSPORT_TYPES = new Set(['FLIGHT', 'TRAIN', 'BUS', 'FERRY', 'OTHER_TRANSPORT'])
const GOOGLE_PLACES_PRICE_LEVELS: ReadonlySet<string> = new Set<GooglePlacesPriceLevel>([
  'PRICE_LEVEL_UNSPECIFIED',
  'PRICE_LEVEL_FREE',
  'PRICE_LEVEL_INEXPENSIVE',
  'PRICE_LEVEL_MODERATE',
  'PRICE_LEVEL_EXPENSIVE',
  'PRICE_LEVEL_VERY_EXPENSIVE',
])

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object'
}

function getString(value: unknown): string | undefined {
  return typeof value === 'string' && value.length > 0 ? value : undefined
}

function getNumber(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined
}

function getGooglePlacesPriceLevel(value: unknown): GooglePlacesPriceLevel | undefined {
  return typeof value === 'string' && GOOGLE_PLACES_PRICE_LEVELS.has(value) ? value as GooglePlacesPriceLevel : undefined
}

function getItemNote(item: TripItineraryItem | TripItemWithoutDay): string | undefined {
  const record = item as unknown as Record<string, unknown>
  return getString(record.note) ?? getString(record.userNote)
}

function getTransportNote(option: TransportOption): string | undefined {
  const record = option as unknown as Record<string, unknown>
  return getString(record.note) ?? getString(record.userNote)
}

function getPlaceTarget(item: TripItineraryItem | TripItemWithoutDay): TripListRow['placeTarget'] {
  const googlePlaceId = item.itemPlaceDetail?.placeId
  if (!googlePlaceId) return undefined
  return { googlePlaceId, placeName: item.name }
}

function getOfferTarget(item: TripItineraryItem | TripItemWithoutDay): TripListOfferTarget | undefined {
  const savedItemData = item.savedItemData

  if (savedItemData?.source === 'offer_experience') {
    return {
      offerId: savedItemData.experienceId,
      offerType: 'experience',
    }
  }

  if (!isHotelSavedData(savedItemData)) return undefined

  const searchParams: TripListOfferTarget['searchParams'] = {}
  if (savedItemData.checkIn) searchParams.checkIn = savedItemData.checkIn
  if (savedItemData.checkOut) searchParams.checkOut = savedItemData.checkOut

  return {
    offerId: savedItemData.offerId,
    offerType: savedItemData.offerType,
    searchParams: Object.keys(searchParams).length > 0 ? searchParams : undefined,
  }
}

function getItemPrice(item: TripItineraryItem | TripItemWithoutDay): TripListPrice {
  const savedItemData = item.savedItemData

  if (isHotelSavedData(savedItemData)) {
    const savedRecord = savedItemData as unknown as Record<string, unknown>
    return {
      price: getNumber(savedItemData.price),
      memberPrice: getNumber(savedRecord.memberPrice),
      currency: getString(savedItemData.currencyCode),
      saleUnit: getString(savedItemData.saleUnit),
    }
  }

  if (isRecord(savedItemData) && savedItemData.source === 'offer_experience') {
    return {
      price: getNumber(savedItemData.price),
      memberPrice: getNumber(savedItemData.memberPrice),
      currency: getString(savedItemData.currencyCode),
      saleUnit: 'person',
    }
  }

  return {
    priceLevel: getGooglePlacesPriceLevel(item.itemPlaceDetail?.priceLevel),
  }
}

function getTransportPrice(option: TransportOption): TripListPrice {
  const savedItemData = isRecord(option.savedItemData)
    ? option.savedItemData as Record<string, unknown>
    : undefined

  return {
    price: getNumber(savedItemData?.price),
    memberPrice: getNumber(savedItemData?.memberPrice),
    currency: getString(savedItemData?.currencyCode),
    saleUnit: 'person',
  }
}

function getItemSubtitle(item: TripItineraryItem | TripItemWithoutDay): string | undefined {
  const savedItemData = item.savedItemData
  const rowType = getItemRowType(item)

  if (rowType !== 'accommodation' && rowType !== 'experience' && rowType !== 'flight') {
    return undefined
  }

  if (isHotelSavedData(savedItemData)) {
    const savedRecord = savedItemData as unknown as Record<string, unknown>
    return getString(savedRecord.propertyName) ?? getString(savedRecord.roomTypeName) ?? item.description
  }

  if (isRecord(savedItemData) && savedItemData.source === 'offer_experience') {
    return getString(savedItemData.location) ?? item.itemPlaceDetail?.primaryTypeDisplayName ?? item.description
  }

  return item.itemPlaceDetail?.primaryTypeDisplayName ?? item.description
}

function getItemRowType(item: TripItineraryItem | TripItemWithoutDay): TripListRowType {
  const savedItemData = item.savedItemData
  if (isHotelSavedData(savedItemData)) return 'accommodation'
  if (isExperienceSavedData(savedItemData)) return 'experience'

  const itemType = String(item.type)
  if (itemType === 'ACCOMMODATION') return 'accommodation'
  if (itemType === 'CRUISE') return 'cruise'
  if (itemType === 'EXPERIENCE') return 'experience'
  if (itemType === 'RESTAURANT' || itemType === 'DINING') return 'dining'
  if (TRANSPORT_TYPES.has(itemType)) return itemType === 'FLIGHT' ? 'flight' : 'transport'

  return getTripListRowTypeFromPlaceCategory(getTripListPlaceCategory({
    primaryType: item.itemPlaceDetail?.primaryType,
    types: item.itemPlaceDetail?.types,
    primaryTypeDisplayName: item.itemPlaceDetail?.primaryTypeDisplayName,
  }))
}

function getTransportRowType(option: TransportOption, transportName: string): TripListRowType {
  const source = isRecord(option.savedItemData)
    ? getString((option.savedItemData as Record<string, unknown>).source)
    : undefined
  if (source === 'le_flight' || source === 'custom_flight') {
    return 'flight'
  }

  const category = getTripListPlaceCategory({
    fallbackText: [
      option.label,
      transportName,
      isRecord(option.savedItemData) ? getString((option.savedItemData as Record<string, unknown>).airline) : undefined,
      isRecord(option.savedItemData) ? getString((option.savedItemData as Record<string, unknown>).flightNumber) : undefined,
      isRecord(option.savedItemData) ? getString((option.savedItemData as Record<string, unknown>).departureAirport) : undefined,
      isRecord(option.savedItemData) ? getString((option.savedItemData as Record<string, unknown>).arrivalAirport) : undefined,
    ].filter(Boolean).join(' '),
  })
  if (category === 'flight') return 'flight'

  return 'transport'
}

function getTransportTitle(option: TransportOption, transportName: string): string {
  return buildFlightName(option, option.label || transportName)
}

function getTransportSubtitle(option: TransportOption, transportName: string): string | undefined {
  if (getTransportRowType(option, transportName) !== 'flight') return undefined
  return option.label && option.label !== transportName ? transportName : undefined
}

function getGroupTitle(intl: IntlShape, stop: TripStop, day?: TripDay): string {
  if (!day) {
    return intl.formatMessage(
      {
        id: 'DDe3x7fD7p2-eLWqeq2C',
        defaultMessage: 'To be planned · {placeName}',
        description: 'Group heading for unplanned itinerary items in the trip planner list view',
      },
      { placeName: stop.placeName },
    )
  }

  return intl.formatMessage(
    {
      id: 'LV82yE9QAcIlSSoP4g6J',
      defaultMessage: '{dayLabel} · {placeName}',
      description: 'Group heading for a day of itinerary rows in the trip planner list view',
    },
    {
      dayLabel: getLocalisedDayLabel(intl, day.dayNumber, day.date),
      placeName: stop.placeName,
    },
  )
}

export function buildTripListRows(data: TripItineraryData | undefined, intl: IntlShape): Array<TripListRow> {
  if (!data?.stops.length) return []

  const rows: Array<TripListRow> = []
  let originalIndex = 0

  for (const stop of data.stops) {
    for (const transport of stop.transports ?? []) {
      for (const option of transport.options ?? []) {
        const rowType = getTransportRowType(option, transport.name)
        rows.push({
          kind: 'transport',
          id: `transport:${option.itemId}`,
          rowType,
          typeLabel: getTripListTypeLabel(intl, rowType),
          title: getTransportTitle(option, transport.name),
          subtitle: getTransportSubtitle(option, transport.name),
          note: getTransportNote(option),
          price: getTransportPrice(option),
          isBooked: Boolean(isRecord(option.savedItemData) ? (option.savedItemData as Record<string, unknown>).isBooked : undefined),
          isBookable: Boolean(option.savedItemData?.source),
          originalIndex,
          stop,
          option,
          transportName: transport.name,
        })
        originalIndex += 1
      }
    }

    for (const day of stop.days ?? []) {
      for (const item of day.items ?? []) {
        if (item.type === 'FLIGHT' && item.savedItemData?.source === 'chat_flight') {
          continue
        }
        const rowType = getItemRowType(item)
        const offerTarget = getOfferTarget(item)
        rows.push({
          kind: 'item',
          id: `item:${item.id}:day:${day.id}`,
          rowType,
          typeLabel: getTripListTypeLabel(intl, rowType),
          title: item.name,
          subtitle: getItemSubtitle(item),
          note: getItemNote(item),
          price: getItemPrice(item),
          isBooked: 'isBooked' in item && Boolean(item.isBooked),
          isBookable: Boolean(offerTarget),
          originalIndex,
          stop,
          day,
          item,
          offerTarget,
          placeTarget: getPlaceTarget(item),
        })
        originalIndex += 1
      }
    }

    for (const item of stop.itemsWithoutDay ?? []) {
      const rowType = getItemRowType(item)
      const offerTarget = getOfferTarget(item)
      rows.push({
        kind: 'item',
        id: `item:${item.id}:unplanned:${stop.id}`,
        rowType,
        typeLabel: getTripListTypeLabel(intl, rowType),
        title: item.name,
        subtitle: getItemSubtitle(item),
        note: getItemNote(item),
        price: getItemPrice(item),
        isBooked: 'isBooked' in item && Boolean(item.isBooked),
        isBookable: Boolean(offerTarget),
        originalIndex,
        stop,
        item,
        offerTarget,
        placeTarget: getPlaceTarget(item),
      })
      originalIndex += 1
    }
  }

  return rows
}

export function filterTripListRows(rows: Array<TripListRow>, filters: TripListFilters): Array<TripListRow> {
  return rows.filter(row => {
    if (filters.types.size > 0 && !filters.types.has(row.rowType)) return false
    if (filters.bookableOnly && !row.isBookable && !row.isBooked) return false
    return true
  })
}

function getComparablePrice(row: TripListRow): number | undefined {
  return row.price.memberPrice ?? row.price.price
}

export function sortTripListRows(rows: Array<TripListRow>, sort: TripListSort | undefined): Array<TripListRow> {
  if (!sort) return rows

  return [...rows].sort((a, b) => {
    let result = 0

    if (sort.field === 'type') {
      result = a.typeLabel.localeCompare(b.typeLabel)
    } else {
      const aPrice = getComparablePrice(a)
      const bPrice = getComparablePrice(b)
      if (aPrice === undefined && bPrice !== undefined) result = 1
      else if (aPrice !== undefined && bPrice === undefined) result = -1
      else if (aPrice !== undefined && bPrice !== undefined) result = aPrice - bPrice
    }

    if (result === 0) {
      result = a.originalIndex - b.originalIndex
    }

    return sort.direction === 'asc' ? result : -result
  })
}

export function groupTripListRows(rows: Array<TripListRow>, sort: TripListSort | undefined, intl: IntlShape): Array<TripListGroup> {
  if (sort) {
    return [{
      id: `sorted:${sort.field}:${sort.direction}`,
      title: intl.formatMessage({
        id: '_-JSAtakph5bof_DV8m2',
        defaultMessage: 'Sorted results',
        description: 'Group heading shown when the trip planner list view is globally sorted',
      }),
      rows,
    }]
  }

  const groups = new Map<string, TripListGroup>()

  for (const row of rows) {
    const groupId = row.day ? `day:${row.day.id}` : `unplanned:${row.stop.id}`
    const existing = groups.get(groupId)
    if (existing) {
      existing.rows.push(row)
      continue
    }

    groups.set(groupId, {
      id: groupId,
      title: getGroupTitle(intl, row.stop, row.day),
      subtitle: row.day?.title,
      rows: [row],
    })
  }

  return Array.from(groups.values()).filter(group => group.rows.length > 0)
}
